// Marketing attribution: where a visitor first came from (utm_* params, a
// campaign short link, or the referring site), kept on the device for 30
// days and sent once with signup. First touch wins so a later direct visit
// doesn't erase the post that actually brought someone in. Storage can be
// unavailable (private mode); then signup simply carries no source.

const KEY = "trim.attribution";
const TTL_MS = 30 * 86400 * 1000;
const CAMPAIGN = "wipe-the-whiteboard";

/** Short links for places where a long UTM URL is awkward: bios, auto-DMs, video end cards, slides. */
export const SHORT_LINKS: Record<string, { utm_source: string; utm_medium: string }> = {
  ig: { utm_source: "instagram", utm_medium: "social" },
  reels: { utm_source: "instagram", utm_medium: "reels" },
  li: { utm_source: "linkedin", utm_medium: "social" },
  reddit: { utm_source: "reddit", utm_medium: "community" },
  hn: { utm_source: "hackernews", utm_medium: "community" },
  yt: { utm_source: "youtube", utm_medium: "shorts" },
  email: { utm_source: "outbound", utm_medium: "email" },
  card: { utm_source: "season-card", utm_medium: "referral" },
};

type Source = Record<string, string> & { at?: string };

function read(): Source | null {
  try {
    const stored = JSON.parse(localStorage.getItem(KEY) ?? "null") as Source | null;
    return stored && Date.now() - Number(stored.at) < TTL_MS ? stored : null;
  } catch {
    return null;
  }
}

function save(source: Source): void {
  if (read()) return;
  try { localStorage.setItem(KEY, JSON.stringify({ ...source, at: String(Date.now()) })); } catch { /* no storage, no attribution */ }
}

/** Call on page load: records utm_* params or an outside referrer, if any. */
export function captureAttribution(): void {
  const params = new URLSearchParams(window.location.search);
  const utm: Source = {};
  for (const key of ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "ref"]) {
    const value = params.get(key);
    if (value) utm[key] = value.slice(0, 200);
  }
  let referrer = "";
  try {
    const ref = document.referrer ? new URL(document.referrer) : null;
    if (ref && ref.host !== window.location.host) referrer = ref.host;
  } catch { /* unparseable referrer */ }
  if (Object.keys(utm).length || referrer) save({ ...utm, ...(referrer ? { referrer } : {}), landing: window.location.pathname });
}

/** Records a short link's source; returns false for an unknown code. */
export function captureShortLink(code: string, content?: string): boolean {
  const link = SHORT_LINKS[code.toLowerCase()];
  if (!link) return false;
  save({ ...link, utm_campaign: CAMPAIGN, ...(content ? { utm_content: content.slice(0, 200) } : {}), landing: `/go/${code}` });
  return true;
}

/** JSON string for the signup body, or undefined when nothing was recorded. */
export function attributionForSignup(): string | undefined {
  const stored = read();
  if (!stored) return undefined;
  const { at: _at, ...rest } = stored;
  return JSON.stringify(rest);
}
